import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  chakra,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import { genBlock } from "@/utils/bem";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";

import { accelerationStore } from "@/stores/acceleration";
import { loginStore } from "@/modules/login/store";
import { langStore } from "@noli/business";

import { LabelText } from "../../styleComponents/Texts";
import "./style.scss";
import { statusStore } from "@/stores/status";

const optionItemStyle = (checked: boolean, disabled: boolean) => ({
  w: "50px",
  h: "26px",
  p: "4px 8px",
  fontSize: "12px",
  lineHeight: "18px",
  rounded: "4px",
  border: "1.5px solid",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  cursor: "pointer",
  fontWeight: 500,
  ...(disabled ? disabledStyle : {}),
  color: checked ? "brand.primary" : "text.placeholder",
  borderColor: checked ? "brand.primary" : "text.placeholder",
  _hover: {
    color: checked ? "brand.primary" : "#cccccc",
    borderColor: checked ? "brand.primary" : "#cccccc",
  },
});

const disabledStyle = {
  opacity: 0.4,
  cursor: "not-allowed",
};

const block = genBlock("c-gen-multi-selector");
const IGenMultiSelector: React.FC<{
  className?: string;
  value: number;
  onChange: (value: number) => void;
  autoDisable: boolean;
}> = ({ className, value = 1, onChange, autoDisable = true }) => {
  const lang = langStore.tgt;
  const { translate } = useTranslate();
  const { isAccelerated } = accelerationStore;
  const [open, setOpen] = useState(false);
  const isChecked = (optionValue: number) => value === optionValue;
  const checkedColor = (optionValue: number) =>
    isChecked(optionValue) ? "brand.primary" : "text.placeholder";
  const isMultiDisabled = useMemo(() => {
    if (!autoDisable) return false;
    return loginStore.isGuest || !isAccelerated;
  }, [isAccelerated, loginStore.isGuest]);

  useEffect(() => {
    if (!isAccelerated && value === 4) {
      onChange(1);
    }
  }, [isAccelerated]);

  function handleClick(value: number) {
    if (value === 4 && isMultiDisabled) {
      return;
    }
    onChange(value);
  }

  return (
    <Flex w="100%" alignItems="center" gap="8px" className={classNames(className, block())}>
      <LabelText className={block("title")} whiteSpace="nowrap" color="#FFFFFF" fontWeight={500}>
        {`${translate(Editor_Words["label_text_generate_count"])}：`}
      </LabelText>
      <Flex className={block({ e: "options" })} wrap="wrap" flex={1} gap="8px">
        <Box
          onClick={() => handleClick(1)}
          __css={{
            ...optionItemStyle(isChecked(1), false),
          }}
        >
          {translate(Editor_Words["generate_count_option_label_template"]).replace("$1", "1")}
        </Box>

        <Popover
          trigger="hover"
          onOpen={() => {
            if (loginStore.isGuest || isAccelerated || !autoDisable) return;
            setOpen(true);
          }}
          // isLazy
          eventListeners={false}
          arrowSize={10}
          arrowShadowColor="none"
          placement="bottom-start"
          offset={[0, 18]}
          boundary={document.body}
          strategy="fixed"
          onClose={() => {
            setOpen(false);
          }}
          closeOnBlur={false}
          isOpen={open}
          modifiers={[
            {
              name: "topLogger",
              enabled: true,
              phase: "main",
              fn: ({ state }) => {
                state.styles.popper.zIndex = "1000";
                return state;
              },
            },
          ]}
        >
          <PopoverTrigger>
            <Box
              tabIndex={0}
              onClick={() => handleClick(4)}
              __css={{
                ...optionItemStyle(isChecked(4), isMultiDisabled),
              }}
            >
              <chakra.div lineHeight="18px">
                {translate(Editor_Words["generate_count_option_label_template"]).replace("$1", "4")}
              </chakra.div>
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              border="none"
              bg="fill.icon"
              rounded="2px"
              w="375px"
              zIndex={1500}
              className={block("tooltip")}
              overflow="hidden"
              __css={{
                overscrollBehavior: "contain",
              }}
            >
              <PopoverArrow bg="fill.icon" />
              <PopoverBody bg="fill.icon" p="8px 12px" color="#ffffff" rounded="2px" w="375px">
                <Box className={block("tooltip-label")} w="100%">
                  {lang !== "zh" ? (
                    "To ensure user experience, the function of generating multiple images at the same time is only available to users using the accelerated channel"
                  ) : (
                    <>
                      由于同时生成多张图片对算力消耗较大，为保证用户体验 生成多张的功能仅对使用{" "}
                      <chakra.div
                        className={block("tooltip-label-gradient-text")}
                        cursor="pointer"
                        onClick={() => accelerationStore.setPaymentModalOpen(true)}
                      >
                        加速通道
                      </chakra.div>{" "}
                      的用户可用
                    </>
                  )}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Flex>
    </Flex>
  );
};

export const GenMultiSelector = chakra(observer(IGenMultiSelector));
